import { useEffect, useSyncExternalStore } from "react";

/**
 * The embedded admin's sections, each at its own path so a reload keeps its place. Home is the
 * app's root (`/`), which the admin's app nav shows as the app's name rather than a listed link;
 * the rest are the nav's links, in order.
 */
export const SCREENS = [
  { name: "Home", path: "/" },
  { name: "Boxes", path: "/boxes" },
  { name: "Products", path: "/products" },
  { name: "Purchases", path: "/purchases" },
  { name: "Items to ship", path: "/ship" },
  { name: "Appearance", path: "/appearance" },
] as const;

type Screen = (typeof SCREENS)[number];

export type ScreenPath = Screen["path"];

/** The sections the app nav lists: every one but Home. */
export const NAV_SCREENS = SCREENS.filter((s) => s.path !== "/");

function screenAt(pathname: string): Screen {
  return SCREENS.find((s) => s.path === pathname) ?? SCREENS[0];
}

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener("popstate", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

/**
 * Opens another section without reloading the app: pushes its path (keeping the query string
 * the admin loaded the app with) and redraws. App Bridge mirrors the path into the admin's URL.
 */
export function navigate(path: string): void {
  window.history.pushState(null, "", `${path}${window.location.search}`);
  for (const listener of listeners) listener();
}

/**
 * The section the URL names. A click in the admin's app nav (`s-app-nav`) dispatches
 * `shopify:navigate` on the link instead of loading the page, so the app pushes the path itself;
 * back and forward arrive as `popstate`.
 */
export function useScreen(): Screen {
  const pathname = useSyncExternalStore(subscribe, () => window.location.pathname);
  useEffect(() => {
    const onNavigate = (event: Event) => {
      const href = event.target instanceof Element ? event.target.getAttribute("href") : null;
      if (href) navigate(new URL(href, window.location.origin).pathname);
    };
    document.addEventListener("shopify:navigate", onNavigate);
    return () => document.removeEventListener("shopify:navigate", onNavigate);
  }, []);
  return screenAt(pathname);
}
